import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CacheManager } from "./cacheManager";
import { BaseService } from "../services/BaseService";

interface EntityQueryResult<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

interface EntityListQueryResult<T> {
  data: T[] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

interface CreateCachedEntityHooksParams<
  T extends { id: number },
  ServiceType extends BaseService<T> = BaseService<T>
> {
  entityName: string;
  service: ServiceType;
  idField?: keyof T;
  enableCacheById?: boolean;
  fieldIndexes?: {
    [K in keyof T]?: (service: ServiceType) => (value: T[K]) => Promise<T[]>;
  };
   singleFieldIndexes?: {
    [K in keyof T]?: (service: ServiceType) => (value: T[K]) => Promise<T | null>;
  };
  staleTimeMs?: number;
  defaultOrderBy?: string;
  defaultOrderDesc?: boolean;
}

export function createCachedEntityHooks<T extends { id: number }>({
  entityName,
  service,
  idField = "id",
  enableCacheById = false,
  fieldIndexes = {},
   singleFieldIndexes = {},
  staleTimeMs = 60 * 60 * 1000,
  defaultOrderBy,
  defaultOrderDesc = true,
}: CreateCachedEntityHooksParams<T>) {
  const baseKey = [entityName];

  const useAll = enableCacheById
    ? (enabled: boolean = true): EntityListQueryResult<T> => {
        const queryClient = useQueryClient();
        const cacheManager = CacheManager.getInstance(queryClient);

        const query = useQuery<T[], Error, T[]>({
          queryKey: baseKey,
          staleTime: staleTimeMs,
          enabled,
          queryFn: async () => {
            const data = defaultOrderBy
              ? await service.getAllSorted(defaultOrderBy, defaultOrderDesc)
              : await service.getAll();
            data.forEach((item) => cacheManager.updateCache(baseKey, item, idField, fieldIndexes));
            return data;
          },
        });

        return {
          data: query.data,
          isLoading: query.isLoading,
          isError: query.isError,
          error: query.error,
          refetch: query.refetch,
        };
      }
    : undefined;

  const useEntityById = enableCacheById
    ? (id: number | undefined): EntityQueryResult<T> => {
        const queryClient = useQueryClient();
        const cacheManager = CacheManager.getInstance(queryClient);

        const query = useQuery<T, Error, T>({
          queryKey: [...baseKey, id],
          staleTime: staleTimeMs,
          enabled: id != null && id > 0,
          queryFn: async (): Promise<T> => {
            const cached = cacheManager.getCachedDataById(baseKey, id!);
            if (cached) return cached as unknown as T;
            const fresh = await service.getById(id!);
            cacheManager.updateCache(baseKey, fresh, idField, fieldIndexes);
            return fresh;
          },
        });

        return {
          data: query.data,
          isLoading: query.isLoading,
          isError: query.isError,
          error: query.error,
          refetch: query.refetch,
        };
      }
    : undefined;

  const useEntityListByField = <K extends keyof T>(
    field: K,
    value: T[K],
    enabled: boolean = true
  ): EntityListQueryResult<T> => {
    const queryClient = useQueryClient();
    const fetcher = fieldIndexes?.[field]?.(service);

    if (!fetcher) throw new Error(`No fetcher defined for field: ${String(field)}`);

    const query = useQuery<T[], Error, T[]>({
      queryKey: [...baseKey, "by", field, value],
      staleTime: staleTimeMs,
      enabled: enabled,
      queryFn: async (): Promise<T[]> => {
        const result = await fetcher(value);
        return result;
      },
    });

    return {
      data: query.data,
      isLoading: query.isLoading,
      isError: query.isError,
      error: query.error,
      refetch: query.refetch,
    };
  };

  const useCreateEntity = () => {
    const queryClient = useQueryClient();
    const cacheManager = CacheManager.getInstance(queryClient);

    return useMutation<T, Error, Partial<T>>({
      mutationFn: (data) => service.create(data),
      onSuccess: (created) => {
        cacheManager.updateCache(baseKey, created, idField, fieldIndexes);
      },
    });
  };

  const useUpdateEntity = () => {
    const queryClient = useQueryClient();
    const cacheManager = CacheManager.getInstance(queryClient);

    return useMutation<T, Error, { id: number; data: Partial<T> }>({
      mutationFn: ({ id, data }) => service.update(id, data),
      onSuccess: (updated) => {
        cacheManager.updateCache(baseKey, updated, idField, fieldIndexes);
      },
    });
  };
  const useEntityByIds = enableCacheById
    ? (ids: number[]): EntityListQueryResult<T> => {
        const queryClient = useQueryClient();
        const cacheManager = CacheManager.getInstance(queryClient);

        const query = useQuery<T[], Error, T[]>({
          queryKey: [...baseKey, "ids", ...ids],
          staleTime: staleTimeMs,
          queryFn: async (): Promise<T[]> => {
            if (!service.getByIds) {
              throw new Error("getByIds not implemented in service");
            }
            return await cacheManager.fetchMissingData(
              baseKey,
              ids,
              (ids) => service.getByIds!(ids),
              idField
            );
          },
        });

        return {
          data: query.data,
          isLoading: query.isLoading,
          isError: query.isError,
          error: query.error,
          refetch: query.refetch,
        };
      }
    : undefined;

  const usePartialUpdateEntity = () => {
    if (!service.partialUpdate) throw new Error("partialUpdate not implemented");
    const queryClient = useQueryClient();
    const cacheManager = CacheManager.getInstance(queryClient);

    return useMutation<void, Error, { id: number; fields: Partial<T> }>({
      mutationFn: ({ id, fields }) => service.partialUpdate!(id, fields),
      onSuccess: async (_, { id }) => {
        const updated = await service.getById(id);
        cacheManager.updateCache(baseKey, updated, idField, fieldIndexes);
        // Force invalidation to ensure UI updates
        queryClient.invalidateQueries({ queryKey: baseKey });
      },
    });
  };

  const useDeleteEntity = () => {
    const queryClient = useQueryClient();
    const cacheManager = CacheManager.getInstance(queryClient);

    return useMutation<void, Error, number>({
      mutationFn: (id) => service.delete(id),
      onSuccess: (_, id) => {
        cacheManager.deleteFromCache(baseKey, id);
      },
    });
  };
const useEntityByField = <K extends keyof T>(
  field: K,
  value: T[K]
): EntityQueryResult<T> => {
  const queryClient = useQueryClient();
  const fetcher = singleFieldIndexes?.[field]?.(service);

  if (!fetcher) throw new Error(`No fetcher defined for single field: ${String(field)}`);

  const query = useQuery<T | null, Error, T | null>({
    queryKey: [...baseKey, "single", field, value],
    staleTime: staleTimeMs,
    queryFn: async (): Promise<T | null> => {
      const result = await fetcher(value);

      // סנכרון עם cache לפי id
      if (result?.id != null) {
        queryClient.setQueryData([...baseKey, result.id], result);
         queryClient.setQueryData(["securePillow", "single", field, value], result);
      }

      return result;
    },
  });

  // נסה למשוך מה־cache לפי id אם יש
  const byId =
    typeof value === "number"
      ? queryClient.getQueryData<T>([...baseKey, value])
      : undefined;

  return {
    data: byId ?? query.data ?? undefined,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
};


  return {
    useAll,
    useEntityById,
    useEntityByIds,
    useEntityListByField,
    useCreateEntity,
    useUpdateEntity,
    usePartialUpdateEntity,
    useDeleteEntity,
      useEntityByField,
  };
}
